import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// One subscription to the native bridge for all readers and layouts. WebKit
// visibility events alone do not cover every native background/lock transition.
const listeners = new Set();
let active = true;
let dispose;
let lastOnline = -Infinity;

export const isAppActive = () => dispose ? active : document.visibilityState !== 'hidden';

export function subscribeAppActivity(listener) {
  if (!dispose) {
    active = document.visibilityState !== 'hidden';
    lastOnline = -Infinity;
    let stopped = false;
    const emit = reason => {
      for (const notify of [...listeners]) notify({ active, reason });
    };
    const change = next => {
      if (stopped || active === next) return;
      active = next;
      emit(next ? 'resume' : 'pause');
    };
    const visibility = () => change(document.visibilityState !== 'hidden');
    const hide = () => change(false);
    const show = event => { if (event.persisted) change(true); };
    const online = () => {
      if (!active || Date.now() - lastOnline < 1000) return;
      lastOnline = Date.now();
      emit('online');
    };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', show);
    window.addEventListener('online', online);
    const native = Capacitor.isNativePlatform()
      ? App.addListener('appStateChange', ({ isActive }) => change(isActive)).catch(() => null) : null;
    dispose = () => {
      stopped = true;
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', show);
      window.removeEventListener('online', online);
      native?.then(handle => handle?.remove()).catch(() => {});
    };
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (!listeners.size) { dispose?.(); dispose = undefined; }
  };
}
